"use client";

import React from "react";
import { Search, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ShopContent() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState("All Categories");
  React.useEffect(() => {
    console.log("Total products loaded:", products.length);
  }, [products]);
  const uniqueCategories = React.useMemo(() => {
    const categories = products.flatMap(p => p.categories.map(c => c.name));
    const unique = Array.from(new Set(categories));
    console.log("Unique categories count:", unique.length);
    return unique;
  }, [products]);
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All Categories" || product.categories.some(cat => cat.name === selectedCategory);
    return matchesSearch && matchesCategory;
  });
  return <div className="space-y-12">
      {}
      <div className="max-w-3xl">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-primary mb-6">
          Explore Our Travel Packages
        </h1>
        <p className="text-lg text-foreground/70 leading-relaxed">
          Discover hand-picked destinations and curated experiences designed for the modern traveler. From serene escapes to bold adventures, find your next journey here.
        </p>
      </div>

      {}
      {(products.length > 0 || loading) && <div className="flex flex-col md:flex-row gap-4 items-center bg-card p-4 rounded-lg border border-border shadow-sm">
          <div className="relative w-full md:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input type="text" placeholder="Search packages..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
          </div>
          <div className="relative w-full md:w-48 max-w-full">
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="w-full appearance-none pl-4 pr-10 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap">
              <option value="All Categories">All Categories</option>
              {uniqueCategories.map((category, i) => <option key={category} value={category} data-index={i}>
                  {category}
                </option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          </div>
          <Button className="w-full md:w-auto px-8" onClick={() => {}}>
            Search
          </Button>
        </div>}

      {}
      {loading ? <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] rounded-lg bg-muted animate-pulse" />)}
        </div> : error ? <div className="py-20 text-center border rounded-lg border-dashed">
          <p className="text-muted-foreground">Unable to load packages at this time.</p>
        </div> : products.length > 0 ? <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, i) => <div key={product.id} data-index={i} className="group bg-card rounded-lg border border-border shadow-md overflow-hidden transition-all hover:shadow-xl">
                <Link to={product.link} className="block relative aspect-[4/3] overflow-hidden">
                  <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/placeholder_travel.png"} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-xl text-primary line-clamp-1">
                      {product.title}
                    </h3>
                    <span className="font-bold text-lg text-secondary">
                      R{product.price}
                    </span>
                  </div>
                  <div className="text-sm text-foreground/60 line-clamp-2 mb-6" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />
                  <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full py-6 rounded-md font-medium hover:bg-primary/90 transition-colors" text="Book Package" showQuantity={false} />
                </div>
              </div>)}
          </div>
          {filteredProducts.length === 0 && <div className="py-20 text-center border rounded-lg border-dashed">
              <p className="text-muted-foreground">No packages match your search. Try adjusting your filters.</p>
            </div>}
        </> : <div className="py-20 text-center border rounded-lg border-dashed">
          <p className="text-muted-foreground">No packages available at the moment.</p>
        </div>}

      {}
      <div className="flex justify-center pt-8">
        <Link to="/ShopPage">
          <Button variant="outline" size="lg" className="px-12 py-6 text-base border-primary text-primary hover:bg-primary hover:text-white transition-all">
            Browse All Packages
          </Button>
        </Link>
      </div>
    </div>;
}
export default function Shop() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const wp_query_params = {
    per_page: 40,
    post_type: "product",
    orderby: "date",
    order: "desc" as const
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1858" id="shop-title" className="relative overflow-hidden bg-background text-foreground py-32 md:py-44">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-full w-24 md:w-40 opacity-40" viewBox="0 0 160 800" fill="none" preserveAspectRatio="none">
        <path d="M40 0 C 70 120, 20 220, 60 340 S 100 560, 50 700 L 50 800" stroke="currentColor" strokeWidth="1" className="text-primary/40" />
      </svg>

      <svg aria-hidden="true" className="pointer-events-none absolute -right-24 top-16 hidden md:block h-[720px] w-[640px] opacity-60" viewBox="0 0 640 720" fill="none">
        <path d="M520 90 C 600 160, 640 280, 600 400 C 560 540, 440 640, 300 660 C 180 680, 60 600, 40 460 C 20 320, 80 180, 200 110 C 320 40, 440 30, 520 90 Z" fill="currentColor" className="text-primary/10" />
      </svg>

      <div ref={contentRef} className={`relative mx-auto max-w-[1320px] px-5 sm:px-8 md:px-12 lg:px-[72px] transition-all duration-700 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <EcommerceProductsProvider wp_query={wp_query}>
          <ShopContent />
        </EcommerceProductsProvider>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-foreground/30" />
    </section>;
}